import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl = 'https://sakuracomic.cn',
  ogType = 'website',
  ogImage = 'https://readdy.ai/api/search-image?query=Beautiful%20anime%20manga%20theme%20with%20cherry%20blossoms%20and%20soft%20pink%20tones%2C%20clean%20minimal%20design%2C%20warm%20atmosphere&width=1200&height=630&seq=og-default&orientation=landscape',
  schema,
}: SEOProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:image', ogImage, true);
    setMeta('og:site_name', '樱花动漫', true);
    setMeta('og:locale', 'zh_CN', true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', ogImage);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    return () => {
      const schemaScript = document.querySelector('script[data-schema="page"]');
      if (schemaScript) schemaScript.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogType, ogImage]);

  useEffect(() => {
    if (!schema) return;

    let schemaScript = document.querySelector('script[data-schema="page"]');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.setAttribute('data-schema', 'page');
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(schema);
  }, [schema]);

  return null;
}

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '樱花动漫',
  url: 'https://sakuracomic.cn',
  description: '樱花动漫是国内领先的日本漫画在线阅读平台，专注日本漫画领域，提供免费漫画在线阅读服务，收录超过5000部漫画作品。',
  email: 'contact@sakuracomic.cn',
  telephone: '400-888-6688',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'CN',
    addressRegion: '上海市',
    addressLocality: '浦东新区',
    streetAddress: '张江高科技园区樱花路888号',
  },
  sameAs: ['https://weibo.com/sakuracomic'],
  logo: 'https://readdy.ai/api/search-image?query=Simple%20elegant%20cherry%20blossom%20logo%20design%20in%20pink%20tones%2C%20minimalist%2C%20clean%20background&width=512&height=512&seq=logo-sakura&orientation=squarish',
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '樱花动漫',
  url: 'https://sakuracomic.cn',
  description: '樱花动漫整合免费漫画与在线漫画资源，专注日本漫画领域，服务广大动漫爱好者。',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://sakuracomic.cn/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};